"use client"
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { createTenantUser, createTenantAdmin, updateTenant, getUserDetail, updateUser, deleteUser, getTaxesAndFees, getTaxeAndFee, createTaxAndFee, updateTaxAndFee, deleteTaxAndFee, getTenants, getTenantRoles, getTenant, getTenantUserDetail, getAllSAInventories, getSAUsers, getAllSADeals, getAllSACalculus, getSALogs, getAppIntegrations, getAppIntegrationApiCatalog, createAppIntegration, updateAppIntegration, rotateAppIntegrationSecret, getAppIntegrationUsers, issueAppIntegrationUserToken } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetAllSAInventories = (search, status, location, altLocation, page, pageSize, tableFilters, tenant) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['sa-inventories', { search, status, location, altLocation, page, pageSize, tableFilters, tenant }],
    queryFn: ({ signal }) => getAllSAInventories(search, status, location, altLocation, page, pageSize, { signal }, tableFilters, tenant),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetAllSADeals = (search, status, page, pageSize, tenant) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['sa-deals', { search, status, page, pageSize, tenant }],
    queryFn: () => getAllSADeals(search, status, "", "", page, pageSize, tenant),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetAllSACalculus = (search, status, page, pageSize, tenant) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['sa-calculus', { search, status, page, pageSize, tenant }],
    queryFn: ({ signal }) => getAllSACalculus(search, status, page, pageSize, tenant),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};
export const useGetSAUsers = (page, pageSize, tenant, search, enabled = true) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["sa-users", page, pageSize, tenant, search],
    queryFn: () => getSAUsers(page, pageSize, tenant, search),
    enabled: enabled,
  });

  return {
    data: data?.data ?? data,
    pagination: data?.pagination ?? null,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetSALogs = (page, pageSize, userId, tenant, enabled = true) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["sa-logs", page, pageSize, userId, tenant],
    queryFn: () => getSALogs(page, pageSize, userId, tenant),
    enabled: enabled,
  });

  return {
    data: data?.data ?? data,
    pagination: data?.pagination ?? null,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetTenantRoles = (tenantId) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant-roles", tenantId],
    queryFn: () => getTenantRoles(tenantId),
    enabled: !!tenantId,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetTenants = (page = 1, pageSize = 10, search = '', status = 'all') => {
  const { data, isLoading, status: queryStatus, isError, error, isSuccess } = useQuery({
    queryKey: ["tenants", page, pageSize, search, status],
    queryFn: () => getTenants(page, pageSize, search, status),
  });

  return {
    data,
    status: queryStatus,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useGetTenant = (id, type = null, page = 1, pageSize = 10, additionalParams = {}) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant", id, type, page, pageSize, additionalParams],
    queryFn: () => getTenant(id, type, page, pageSize, additionalParams),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useCreateTelantAdmin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (formData) => createTenantAdmin(formData),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });

      console.log("data123123", data)
      // Show success notification
      showNotification('success', 'Customer created successfully!');

      // Navigate immediately without setTimeout
      navigate(`/tenant/add/${data?.code}`);
    },
    onError: (error) => {
      console.error("Error creating tenant admin:", error);

      // Show error notification
      let errorMessage = 'Error creating customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useGetUserDetail = (id) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["user-detail", id],
    queryFn: () => getUserDetail(id),
    enabled: !!id,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateTenantUser = (tenantId, onSuccessCallback) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => createTenantUser(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      showNotification('success', 'User created successfully!');
      if (onSuccessCallback) {
        onSuccessCallback(data);
      } else {
        navigate(`/tenant`);
      }
    },
    onError: (error) => {
      console.error("Error creating tenant user:", error);

      let errorMessage = 'Error creating user. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateTenant = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateTenant(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
      showNotification('success', 'Tenant updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating tenant:", error);

      let errorMessage = 'Error updating tenant. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGetTenantUserDetail = (id, tenantId) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant-user-detail", id, tenantId],
    queryFn: () => getTenantUserDetail(id, tenantId),
    enabled: !!id && !!tenantId,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useUpdateUser = (tenantId) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateUser(tenantId, id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["user-detail"] });

      // Show success notification
      showNotification('success', 'Customer updated successfully!');

    },
    onError: (error) => {
      console.error("Error updating user:", error);

      // Show error notification
      let errorMessage = 'Error updating customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });

      // Show success notification
      showNotification('success', 'Customer deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting user:", error);

      // Show error notification
      let errorMessage = 'Error deleting customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGetTaxesAndFees = () => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["taxes-and-fees"],
    queryFn: () => getTaxesAndFees(),
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};



export const useGetTaxeAndFee = (id) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["taxe-and-fee", id],
    queryFn: () => getTaxeAndFee(id),
    enabled: !!id,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useCreateTaxAndFee = ({ onClose, type }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (formData) => createTaxAndFee(formData),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["taxes-and-fees"] });
      await queryClient.invalidateQueries({ queryKey: ["sales-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });
      showNotification('success', `${type} created successfully!`);
      if (onClose) onClose();
    },
    onError: (error) => {
      console.error("Error creating taxe and fee:", error);
      let errorMessage = `Error creating ${type}. Please try again.`;
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useUpdateTaxAndFee = ({ onClose, type }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateTaxAndFee(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["taxes-and-fees"] });
      await queryClient.invalidateQueries({ queryKey: ["sales-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });

      // Show success notification
      showNotification('success', `${type} updated successfully!`);

      // Navigate immediately without setTimeout
      if (onClose) onClose();
    },
    onError: (error) => {
      console.error("Error updating taxe and fee:", error);

      // Show error notification
      let errorMessage = `Error updating ${type}. Please try again.`;
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteTaxAndFee = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteTaxAndFee(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["taxes-and-fees"] });
      await queryClient.invalidateQueries({ queryKey: ["sales-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });

      // Show success notification
      showNotification('success', 'Tax/Fee deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting tax/fee:", error);

      // Show error notification
      let errorMessage = 'Error deleting tax/fee. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGetAppIntegrations = (tenant) => {
  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    queryKey: ["sa-app-integrations", tenant],
    queryFn: () => getAppIntegrations(tenant),
  });

  return {
    data: data?.data ?? data ?? [],
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};

export const useGetAppIntegrationApiCatalog = (enabled = true) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ["sa-app-integration-api-catalog"],
    queryFn: () => getAppIntegrationApiCatalog(),
    enabled,
  });

  return { data, isLoading, isError, error, isSuccess };
};

export const useCreateAppIntegration = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (payload) => createAppIntegration(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sa-app-integrations"] });
      showNotification("success", "App integration created");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        "Failed to create app integration";
      showNotification("danger", message);
    },
  });
};

export const useUpdateAppIntegration = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateAppIntegration(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sa-app-integrations"] });
      showNotification("success", "App integration updated");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        "Failed to update app integration";
      showNotification("danger", message);
    },
  });
};

export const useRotateAppIntegrationSecret = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => rotateAppIntegrationSecret(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sa-app-integrations"] });
      showNotification("success", "Client secret rotated");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        "Failed to rotate secret";
      showNotification("danger", message);
    },
  });
};

export const useGetAppIntegrationUsers = (id, enabled = true) => {
  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    queryKey: ["sa-app-integration-users", id],
    queryFn: () => getAppIntegrationUsers(id),
    enabled: !!id && enabled,
  });

  return {
    data: data?.data ?? data ?? [],
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};

export const useIssueAppIntegrationUserToken = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => issueAppIntegrationUserToken(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sa-app-integration-users"] });
      showNotification("success", "User access token issued");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        "Failed to issue user token";
      showNotification("danger", message);
    },
  });
};